import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Flag from "@/components/Flag";

export default function SuperLanguages() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { setData((await api.get("/platform/languages")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (code) => {
        const set = new Set(data.enabled);
        if (set.has(code)) {
            if (set.size <= 1) return toast.error("At least one language required");
            set.delete(code);
        } else { set.add(code); }
        const enabled = Array.from(set);
        const def = enabled.includes(data.default) ? data.default : enabled[0];
        setData({ ...data, enabled, default: def });
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/platform/languages", { enabled: data.enabled, default: data.default });
            toast.success("Saved");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <Topbar title={t("platform.global_languages")} subtitle="Platform-wide languages" />
            <div className="flex-1 overflow-auto p-6">
                {!data ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : (
                    <div className="akm-card max-w-2xl p-5 space-y-4" data-testid="super-languages">
                        <div>
                            <div className="text-[13px] font-semibold tracking-tight">{t("platform.global_languages")}</div>
                            <div className="mt-1 text-[12px] text-muted-foreground">{t("platform.global_languages_help")}</div>
                        </div>

                        <div className="space-y-1.5">
                            {data.supported.map((lang) => {
                                const checked = data.enabled.includes(lang.code);
                                return (
                                    <label key={lang.code}
                                           className="flex items-center gap-3 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/40">
                                        <input type="checkbox" checked={checked}
                                               onChange={() => toggle(lang.code)}
                                               data-testid={`super-lang-${lang.code}`} />
                                        <Flag country={lang.country} className="!h-4" />
                                        <div className="flex-1">
                                            <div className="text-[13px] font-medium">{lang.label}</div>
                                            <div className="text-[10px] text-muted-foreground">{lang.english}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        <div>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                                {t("settings.default_language")}
                            </div>
                            <select value={data.default}
                                    onChange={(e) => setData({ ...data, default: e.target.value })}
                                    className="akm-input text-[13px] max-w-xs"
                                    data-testid="super-default-language">
                                {data.enabled.map((c) => {
                                    const lang = data.supported.find((l) => l.code === c);
                                    return <option key={c} value={c}>{lang?.label || c}</option>;
                                })}
                            </select>
                        </div>

                        <div className="pt-2">
                            <button onClick={save} disabled={busy} className="akm-btn-primary"
                                    data-testid="super-languages-save">
                                {t("settings.save_changes")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
